import { random, extend, colord, Colord } from 'colord';
import cmykPlugin from 'colord/plugins/cmyk';

import { ColorType, ICMYK, IHSL, IRGB, IHSV, IPickerColor } from './types';

export const matches = (input: string, matchers: RegExp[]) => {
  for (const matcher of matchers) {
    if (matcher.test(input)) {
      return true;
    }
  }
  return false;
};

const getNumbersList = (input: string, regex = /,|%/g) => {
  return input.replace(regex, ' ').split(' ').filter(Boolean).map(Number);
};

const hexMatchers = [
  /^\s*#([0-9a-f]{3})\s*$/i,
  /^\s*#([0-9a-f]{6})\s*$/i,
  /^\s*([0-9a-f]{3})\s*$/i,
  /^\s*([0-9a-f]{6})\s*$/i,
];

const rgbMatchers = [
  // comma as delimiter
  /^\s*([+-]?\d{0,3}\.?\d+)\s*,\s*([+-]?\d{0,3}\.?\d+)\s*,\s*([+-]?\d{0,3}\.?\d+)\s*$/i,
  /^\s*rgb\(\s*([+-]?\d{0,3}\.?\d+)\s*,\s*([+-]?\d{0,3}\.?\d+)\s*,\s*([+-]?\d{0,3}\.?\d+)\s*\)\s*$/i,
  // spaces as delimiters
  /^\s*([+-]?\d{0,3}\.?\d+)\s+([+-]?\d{0,3}\.?\d+)\s+([+-]?\d{0,3}\.?\d+)\s*$/i,
  /^\s*rgb\(\s*([+-]?\d{0,3}\.?\d+)\s+([+-]?\d{0,3}\.?\d+)\s+([+-]?\d{0,3}\.?\d+)\s*\)\s*$/i,
];

const hslMatchers = [
  // comma as delimiter
  /^\s*(\d{0,3}\.?\d+)\s*,\s*(\d{0,3}\.?\d+%)\s*,\s*(\d{0,3}\.?\d+%)\s*$/i,
  /^\s*hsl\(\s*(\d{0,3}\.?\d+)\s*,\s*(\d{0,3}\.?\d+%)\s*,\s*(\d{0,3}\.?\d+%)\s*\)\s*$/i,
  // spaces as delimiters
  /^\s*(\d{0,3}\.?\d+)\s+(\d{0,3}\.?\d+)%\s+(\d{0,3}\.?\d+)%\s*$/i,
  /^\s*hsl\(\s*(\d{0,3}\.?\d+)\s+(\d{0,3}\.?\d+)%\s+(\d{0,3}\.?\d+)%\s*\)\s*$/i,
];

const cmykMatchers = [
  // spaces as delimiter
  /^\s*(\d{0,3}\.?\d+)\s+(\d{0,3}\.?\d+)\s+(\d{0,3}\.?\d+)\s+(\d{0,3}\.?\d+)\s*$/i,
  /^\s*(device-)?cmyk\s*\((\d{0,3}\.?\d+)\s+(\d{0,3}\.?\d+)\s+(\d{0,3}\.?\d+)\s+(\d{0,3}\.?\d+)\s*\)\s*$/i,
  // comma as delimiter
  /^\s*(\d{0,3}\.?\d+)\s*,\s*(\d{0,3}\.?\d+)\s*,\s*(\d{0,3}\.?\d+)\s*,\s*(\d{0,3}\.?\d+)\s*$/i,
  /^\s*(device-)?cmyk\s*\((\d{0,3}\.?\d+)\s*,\s*(\d{0,3}\.?\d+)\s*,\s*(\d{0,3}\.?\d+)\s*,\s*(\d{0,3}\.?\d+)\s*\)\s*$/i,
];

extend([cmykPlugin]);

export default class Color {
  color: Colord;

  constructor(str: string | undefined = undefined) {
    if (str) {
      this.color = colord(str);
      return;
    }

    this.color = random();
  }

  static getColorType(input: string) {
    if (Color.isValidHex(input)) {
      return ColorType.HEX;
    }

    if (Color.isValidRgb(input)) {
      return ColorType.RGB;
    }

    if (Color.isValidHsl(input)) {
      return ColorType.HSL;
    }

    if (Color.isValidCmyk(input)) {
      return ColorType.CMYK;
    }

    return ColorType.UNKNOWN;
  }

  static fromHexString(str: string) {
    const trimmed = str.trim();

    if (trimmed.startsWith('#')) {
      return new Color(trimmed);
    }

    return new Color(`#${trimmed}`);
  }

  static fromHslString(str: string) {
    const trimmed = str.trim();
    let nums: number[];

    if (/^hsl/i.test(trimmed)) {
      nums = getNumbersList(trimmed, /,|%|\(|\)|(hsl)/g);
    } else {
      nums = getNumbersList(trimmed);
    }

    return new Color(`hsl(${nums[0]},${nums[1]}%,${nums[2]}%)`);
  }

  static fromRgbString(str: string) {
    const trimmed = str.trim();

    if (/^rgb/i.test(trimmed)) {
      return new Color(trimmed);
    }

    const nums = getNumbersList(str);

    return new Color(`rgb(${nums[0]},${nums[1]},${nums[2]})`);
  }

  static fromCmykString(str: string) {
    const trimmed = str.trim();
    let nonPrefixed: string;

    if (/^device-cmyk/i.test(trimmed)) {
      nonPrefixed = trimmed.replace(/device-cmyk|\(|\)/gi, '');
    } else if (/^cmyk/i.test(trimmed)) {
      nonPrefixed = trimmed.replace(/cmyk|\(|\)/gi, '');
    } else {
      nonPrefixed = trimmed;
    }
    const nums = getNumbersList(nonPrefixed);
    const cmyk = `device-cmyk(${nums[0] | 0}% ${nums[1] | 0}% ${nums[2] | 0}% ${nums[3] | 0}%)`;

    return new Color(cmyk);
  }

  static isValidHex(str: string) {
    return matches(str, hexMatchers);
  }
  static isValidRgb(str: string) {
    return matches(str, rgbMatchers);
  }
  static isValidHsl(str: string) {
    return matches(str, hslMatchers);
  }
  static isValidCmyk(str: string) {
    return matches(str, cmykMatchers);
  }

  public toHslString() {
    const { h, s, l } = this.hsl;

    return `${h}, ${s}%, ${l}%`;
  }

  public toCmykString() {
    const { c, m, y, k } = this.cmyk;

    return `${c}, ${m}, ${y}, ${k}`;
  }

  public toRgbString() {
    const { r, g, b } = this.rgb;

    return `${r}, ${g}, ${b}`;
  }

  public toHexString() {
    return `#${this.hex}`;
  }

  public getBlackOrWhiteHexByLightness() {
    return this.color.isLight() ? '#000' : '#fff';
  }

  public brightness() {
    return this.color.brightness();
  }

  public getPickerColor(): IPickerColor {
    return {
      hex: this.toHexString(),
      rgb: this.rgb,
      hsl: this.hsl,
    };
  }

  get hex(): string {
    return this.color.toHex();
  }

  get hsl(): IHSL {
    return this.color.toHsl();
  }

  get rgb(): IRGB {
    return this.color.toRgb();
  }

  get hsv(): IHSV {
    return this.color.toHsv();
  }

  get cmyk(): ICMYK {
    return this.color.toCmyk();
  }
}
